"use client";

import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { bindActionCreators } from "redux";
import * as actionTypes from "../../store/actions";
import * as actionCreators from "../../store/action-creators";
import { useFormik } from "formik";
import * as YUP from "yup";
import { Dropdown } from "primereact/dropdown";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { ConfirmDialog } from "primereact/confirmdialog";
import { Dialog } from "primereact/dialog"; // For <ConfirmDialog /> component
import { Toast } from "primereact/toast";
import Spinner from "../../components/spinner/spinner";
import "../../globals.css";
import styles from "./CreateCreditInvoicesForm.module.scss";
import { unstable_usePrompt } from "react-router-dom";
import { appInsights } from "../../AppInsights";
import ProcessCreditInvoices from "../../components/process-credit-invoices/process-credit-invoices";
import { sortData } from "../../constants/dataSort";
import { fetchProcessedData } from "../../services/creditInvoiceService";
import LoginUserDataContext from "../../store/login";

const initialValue = {
  creditPercentage: null,
  region: null,
  file: null,
  pasteContent: null
};

const CreateCreditInvoicesForm = ({ isFileUploaded }) => {
  const InvoiceCreditPercentageFilterDropdownOptions = [
    { label: "25%", value: 25 },
    { label: "50%", value: 50 },
    { label: "100%", value: 100 },
    { label: "125%", value: 125 }
  ];
  appInsights.trackPageView({ name: "Create Credit Invoice screen visited" });
  const fileInputRef = useRef(null);
  const [isFileRequired, setIsFileRequired] = useState(true);
  const [fileFormatTypes, setFileFormatTypes] = useState(
    ".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel, text/plain"
  );
  const [operationPerformed, setOperationPerformed] = useState(null);
  const inputRef = useRef(null);
  const toast = useRef(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [restrictNavigation, setRestrictNavigation] = useState(false);
  const [pasteError, setPasteError] = useState("");
  const [excelUploaded, setExcelUploaded] = useState(false);
  const [excelUploadedFileName, setExcelUploadedFileName] = useState("");
  const [excelPasted, setExcelPasted] = useState(false);
  const [fileContentUploaded, setFileContentUploaded] = useState(false);
  const [processedData, setProcessedData] = useState([]);
  const [processedDataStarted, setProcessedDataStarted] = useState(false);
  const [showSpinner, setShowSpinner] = useState(false);
  const [userData, setUserData] = useContext(LoginUserDataContext);
  const [defaultRegion, setDefaultRegion] = useState([]);
  const dispatch = useDispatch();
  const { getRegions } = bindActionCreators(actionCreators, dispatch);

  const regions = useSelector((state) => {
    const data = state?.orderReducer?.regions;
    const sortedData = sortData("regionNameAbbr", data);
    return sortedData;
  });

  const handleFileUploadStatus = useCallback(() => {
    isFileUploaded(restrictNavigation);
  }, [isFileUploaded, restrictNavigation]);

  useEffect(() => {
    handleFileUploadStatus();
  }, [handleFileUploadStatus]);

  useEffect(() => {
    getRegions();
  }, []);

  const validationSchema = YUP.object().shape({
    creditPercentage: YUP.number()
      .nullable()
      .required("Credit (%) is required"),
    region: YUP.object().nullable().required("Region is required"),
    file: isFileRequired
      ? YUP.string().nullable().required("File is required")
      : YUP.string().nullable(),
    pasteContent: isFileRequired
      ? YUP.string().nullable()
      : YUP.string().required("Excel data is required")
  });

  const formik = useFormik({
    initialValues: initialValue,
    validationSchema: validationSchema,
    onSubmit: (values, { resetForm }) => {
      submitProcessData(values);
      setShowSpinner(true);
    }
  });

  const clearForm = () => {
    try {
      formik.resetForm();
      setProcessedData([]);
      clearSelectedFile();
      formik.setFieldValue("creditPercentage", "");
      formik.setFieldValue("region", defaultRegion ? defaultRegion : regions[0]);
      setRestrictNavigation(false);
    } catch (error) {
      appInsights.trackException({
        exception: error,
        properties: {
          additionalInfo: "Exception while clearing create credit invoice form"
        },
        measurements: {
          severityLevel: 2
        }
      });
    }
  };
  useEffect(() => {
    if (userData?.regionId != null) {
      let filteredRegion = regions?.filter(
        (item) => item?.regionId == userData?.regionId
      );
      setDefaultRegion(filteredRegion[0]);
      formik.setFieldValue("region", filteredRegion[0]);
    } else {
      formik.setFieldValue("region", regions[0]);
    }
    setRestrictNavigation(false);
  },[]);
  const submitProcessData = async (formbody) => {
    try {
      const formData = new FormData();
      formData.append("Percentage", formbody?.creditPercentage);
      formData.append("PastedContent", formbody?.pasteContent);
      formData.append("File", formbody?.file);
      formData.append("Region", formbody?.region?.regionNameAbbr);
      setProcessedDataStarted(true);
      const result = await fetchProcessedData(formData);
      setProcessedData(result);
      setPasteError("");
      setProcessedDataStarted(false);
      setRestrictNavigation(true);
      toast?.current?.show({
        severity: "success",
        summary: "Success",
        detail: "Data Processed Successfully",
        life: 4000
      });
    } catch (error) {
      setProcessedDataStarted(false);
      toast?.current?.show({
        severity: "error",
        summary: "Error",
        detail: error?.message || "Internal Server Error",
        life: 4000
      });
      appInsights.trackException({
        exception: error,
        properties: {
          additionalInfo: "Error while submitting create credit invoice form"
        },
        measurements: {
          severityLevel: 2
        }
      });
    } finally {
      setShowSpinner(false);
    }
  };

  const browseFiles = () => {
    setOperationPerformed("upload");
    if (processedData?.length > 0) {
      setShowConfirmDialog(true);
    } else {
      setShowConfirmDialog(false);
      fileInputRef?.current.click();
      setPasteError("");
    }
  };

  const onFileSelected = (event) => {
    setFileContentUploaded(true);
    setExcelUploaded(true);
    setExcelPasted(false);
    setExcelUploadedFileName(event?.target?.files[0]?.name);
    setIsFileRequired(true);
    formik.setFieldValue("file", event?.target?.files[0]);
    setProcessedData([]);
    setRestrictNavigation(false);
    setPasteError("");
  };

  const handleDragOver = (event) => {
    event.preventDefault();
  };

  const handleDrop = (event) => {
    event?.preventDefault();
    // setFiles(event.dataTransfer.files)
    if (event?.dataTransfer?.files?.length !== 1) {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Please select single file.",
        life: 4000
      });
      return;
    }
    const fileName = event?.dataTransfer?.files[0]?.name;
    const fileType = fileName?.substring(fileName.lastIndexOf("."));

    if (fileFormatTypes?.includes(fileType?.toLocaleLowerCase())) {
      setFileContentUploaded(true);
      setExcelUploaded(true);
      setExcelPasted(false);
      setExcelUploadedFileName(fileName);
      setIsFileRequired(true);
      formik.setFieldValue("file", event?.dataTransfer?.files[0]);
      setProcessedData([]);
      setRestrictNavigation(false);
      setPasteError("");
    } else {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: `Please select ${fileFormatTypes} file format.`,
        life: 4000
      });
    }
  };

  const isExcelData = (data) => {
    if (data.endsWith("\n")) {
      data = data.slice(0, -1);
    }
    if (!data.includes("\t") && !data.includes(",") && !data.includes(";")) {
      return data.split("\n").length >= 2;
    }
    // Split pasted data into rows based on newline character
    const rows = data?.split("\n")?.map((row) => row);

    // Check if there are at least two rows
    if (rows?.length < 2) {
      return false;
    }

    const columnCounts = data.includes("\t")
      ? rows?.map((row) => row?.split("\t").length)
      : data.includes(",")
      ? rows?.map((row) => row?.split(",").length)
      : data.includes(";")
      ? rows?.map((row) => row?.split(";").length)
      : "";
    // const columnCounts = rows?.map((row) => row.split("\t").length)
    const uniqueColumnCounts = new Set(columnCounts);
    return uniqueColumnCounts?.size >= 1;
  };

  const handlePasteButtonClick = async () => {
    setIsFileRequired(false);
    setExcelUploaded(false);
    setExcelPasted(true);
    setOperationPerformed("paste");
    setExcelUploadedFileName("");
    if (processedData?.length > 0) {
      setShowConfirmDialog(true);
    } else {
      setShowConfirmDialog(false);
      if (navigator?.clipboard && navigator?.clipboard.readText) {
        try {
          inputRef.current.focus();
          const text = await navigator?.clipboard.readText();
          if (isExcelData(text)) {
            setFileContentUploaded(true);
            formik.setFieldValue("pasteContent", text);
            setProcessedData([]);
            setRestrictNavigation(false);
            setPasteError("");
            return;
          } else {
            clearSelectedFile();
            setPasteError("Please paste the valid content.");
            return;
            // Handle non-Excel data here
          }
        } catch (error) {
          console.warn("navigator.clipboard failed:", error);
          appInsights.trackException({
            exception: error,
            properties: {
              additionalInfo:
                "Exception while pasting file in create credit invoice screen"
            },
            measurements: {
              severityLevel: 2
            }
          });
        }
      }
    }
  };

  const clearSelectedFile = () => {
    setIsFileRequired(true);
    formik.setFieldValue("file", null);
    formik.setFieldValue("pasteContent", "");
    if (excelUploaded) {
      formik.handleChange({
        target: {
          name: "file", // Name of the field you want to update
          value: null // New value you want to set (in this case, null)
        }
      });
    } else {
      formik.handleChange({
        target: {
          name: "pasteContent", // Name of the field you want to update
          value: "" // New value you want to set (in this case, null)
        }
      });
    }
    setFileContentUploaded(false);
    setExcelPasted(false);
    setExcelUploaded(false);
    setExcelUploadedFileName("");
    setPasteError("");
  };

  const accept = () => {
    try {
      setProcessedData(() => {
        const newData = [];
        return newData;
      });
      setRestrictNavigation(false);
      autoOpenOrautoPasteContent();
    } catch (error) {
      appInsights.trackException({
        exception: error,
        properties: {
          additionalInfo: "Error clearing the processed data content"
        },
        measurements: {
          severityLevel: 2
        }
      });
    }
  };

  const autoOpenOrautoPasteContent = async () => {
    try {
      if (operationPerformed === "upload") {
        setShowConfirmDialog(false);
        fileInputRef?.current?.click();
        setPasteError("");
      } else if (operationPerformed === "paste") {
        setShowConfirmDialog(false);
        if (navigator?.clipboard && navigator?.clipboard?.readText) {
          try {
            inputRef?.current.focus();
            const text = await navigator?.clipboard.readText();
            if (isExcelData(text)) {
              setFileContentUploaded(true);
              formik.setFieldValue("pasteContent", text);
              setProcessedData([]);
              setRestrictNavigation(false);
              setPasteError("");
              return;
            } else {
              clearSelectedFile();
              setPasteError("Please paste the valid content.");
              return;
              // Handle non-Excel data here
            }
          } catch (error) {
            console.warn("navigator.clipboard failed:", error);
            appInsights.trackException({
              exception: error,
              properties: {
                additionalInfo:
                  "Exception while pasting file in create invoice screen"
              },
              measurements: {
                severityLevel: 2
              }
            });
          }
        }
      }
    } catch (error) {
      appInsights.trackException({
        exception: error,
        properties: {
          additionalInfo:
            "Error while auto Pasting or auto file upload browser open"
        },
        measurements: {
          severityLevel: 2
        }
      });
    }
  };

  const handleConfirmation = () => {
    if (processedData?.length > 0) {
      setShowConfirmDialog(true);
    } else {
      setShowConfirmDialog(false);
    }
  };

  // unstable_usePrompt({
  //   message:
  //     "Changes you made may not be saved. Are you sure you want to proceed?",
  //   when: processedData?.length > 0 && restrictNavigation
  // });

  return (
    <>
      {showSpinner && <Spinner />}
      <form onSubmit={formik.handleSubmit}>
        {processedDataStarted && <Spinner />}
        <div className="createInvoiceContainer">
          <div className="grid ">
            <div className="col-12 lg:col-4 lg:col-offset-4">
              <div className={` ${styles.dropdownContainer}`}>
                <label htmlFor="region">
                  <span className="star">*</span>
                  <span className={styles.formLabel}>Region </span>
                </label>
                <Dropdown
                  value={formik.values.region}
                  onChange={(e) => {
                    formik.setFieldValue("region", e?.value);
                    clearSelectedFile();
                  }}
                  options={regions}
                  filter
                  optionLabel="regionNameAbbr"
                  placeholder="Region"
                  className={styles.dropdown}
                  onBlur={formik.handleBlur}
                />
              </div>
              {formik.errors.region && formik.touched.region && (
                <small className={styles.errorMsg}>
                  {formik.errors.region}
                </small>
              )}
            </div>
            <div className="col-12 lg:col-4 ">
              <div className={` ${styles.dropdownContainer}`}>
                <label htmlFor="creditPercentage">
                  <span className="star">*</span>
                  <span className={styles.formLabel}>Credit (%) </span>
                </label>
                <Dropdown
                  value={formik.values.creditPercentage}
                  onChange={(e) =>
                    formik.setFieldValue("creditPercentage", e?.value)
                  }
                  placeholder="Credit(%)"
                  options={InvoiceCreditPercentageFilterDropdownOptions}
                  optionLabel="label"
                  className={styles.dropdown}
                  onBlur={formik.handleBlur}
                />
              </div>
              {formik.errors.creditPercentage &&
                formik.touched.creditPercentage && (
                  <small className={styles.errorMsg}>
                    {formik.errors.creditPercentage}
                  </small>
                )}
            </div>
          </div>
          <div className="col-12">
            <div
              className={styles.fileUploadContainer}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
            >
              <div className={styles.fileUploadBtnContainer}>
                {!fileContentUploaded && (
                  <div className={styles.btnBlock}>
                    <input
                      type="file"
                      id="fileInput"
                      ref={fileInputRef}
                      value={formik.values.file}
                      onChange={onFileSelected}
                      accept={fileFormatTypes}
                      style={{ display: "none" }}
                    />
                    <Button
                      label="Upload"
                      type="button"
                      size="small"
                      icon="pi pi-upload"
                      onClick={browseFiles}
                      className={styles.primaryBtn}
                    />
                  </div>
                )}
                {!fileContentUploaded && (
                  <div>
                    <p className={styles.ashFont}>Or</p>
                  </div>
                )}
                <div
                  className={`${styles.btnBlock} ${
                    fileContentUploaded && excelPasted
                      ? styles.textAreaBlock
                      : ""
                  }`}
                >
                  {fileContentUploaded && excelPasted && (
                    <textarea
                      ref={inputRef}
                      id="pasteContent"
                      type="text"
                      onChange={formik.handleChange}
                      value={formik.values.pasteContent}
                      className={styles.textAreaContainer}
                    />
                  )}

                  {!fileContentUploaded && (
                    <div>
                      <textarea
                        ref={inputRef}
                        type="text"
                        value={formik.values.pasteContent}
                        readOnly
                        className={styles.textAreaContainer}
                        style={{ display: "none" }}
                      />
                      <Button
                        label="Paste"
                        icon="pi pi-pencil"
                        size="small"
                        type="button"
                        onClick={handlePasteButtonClick}
                        outlined
                        className={styles.secondaryBtn}
                      />
                    </div>
                  )}
                </div>
                {fileContentUploaded && excelUploaded && (
                  <span className={styles.uploadedExcel}>
                    <i className="pi pi-info-circle" />
                    <p className={styles.uploadedExcelTitle}>
                      {excelUploadedFileName + " added."}
                    </p>
                    <i
                      className="pi pi-times-circle"
                      onClick={clearSelectedFile}
                      style={{ cursor: "pointer" }}
                    />
                  </span>
                )}
              </div>
              {!fileContentUploaded && (
                <div>
                  <p className={styles.ashFont}>
                    Drag & Drop, Upload or Paste Data
                  </p>

                  <small className={styles.ashFont}>Upload ExcelTagged</small>
                </div>
              )}
            </div>
          </div>
          <div className="col-12">
            <div className={`grid ${styles.creatFormButnContainer}`}>
              <div
                className={`col-12 lg:col-5 ${styles.clearButtonAndErrorContainer}`}
              >
                {fileContentUploaded && (
                  <Button
                    label="Clear"
                    onClick={clearSelectedFile}
                    type="button"
                    size="small"
                    severity="danger"
                    outlined
                  />
                )}
                {pasteError.trim() !== "" && (
                  <div
                    className={` ${styles.errorMsg} ${styles.errorContainer}`}
                  >
                    <span>{pasteError}</span>
                  </div>
                )}
              </div>

              <div className={`col-12 lg:col-7   ${styles.alignRight}`}>
                <div className="grid justify-content-end ">
                  <div className="col-12 lg:col-4 lg:mr-2 processData">
                    <Button
                      label="Fetch Data"
                      type="submit"
                      size="small"
                      className={styles.primaryBtn}
                      disabled={!formik.isValid}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </form>
      {showConfirmDialog && (
        <ConfirmDialog
          visible={showConfirmDialog}
          onHide={() => setShowConfirmDialog(false)}
          message="Changes you made may not be saved. Are you sure you want to proceed?"
          header="Confirmation"
          icon="pi pi-exclamation-triangle"
          style={{ width: "50vw", color: "red" }}
          breakpoints={{ "1100px": "75vw", "960px": "100vw" }}
          accept={accept}
        ></ConfirmDialog>
      )}
      <Toast ref={toast} position="center" />
      <ProcessCreditInvoices
        processedData={processedData}
        clearForm={clearForm}
        handleConfirmation={handleConfirmation}
        setRestrictNavigation={setRestrictNavigation}
      />
    </>
  );
};

export default CreateCreditInvoicesForm;
